import { useEffect, useState } from "react";
import API from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export default function DunningDemoDialog({ open, onClose, onDone }) {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [days, setDays] = useState(9);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    API.get("/platform/payment-recovery/tenants")
      .then(({ data }) => { setTenants(data); const first = data.find((t) => !t.past_due) || data[0]; setTenantId(first?.tenant_id || ""); })
      .catch(() => toast.error("Couldn't load workspaces"));
  }, [open]);

  const selected = tenants.find((t) => t.tenant_id === tenantId);

  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/platform/payment-recovery/simulate", { tenant_id: tenantId, days_past_due: days, amount: amount === "" ? null : Number(amount) });
      toast.success(`${data.company_name || data.slug} is now ${data.locked ? "read-only (simulated)" : `past due — ${data.days_to_lock} day(s) to read-only (simulated)`}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the simulation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="dunning-demo-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Dunning demo switch</DialogTitle>
          <DialogDescription className="text-xs">
            Put a workspace into a simulated failed-renewal state — no Stripe involved. Its users see the failed-payment banner, the grace countdown and (past the grace period) the read-only lock; chases and the trend chart work as normal. Everything is undone with "End simulation".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <label className="block">
            <span className="text-muted-foreground">Workspace</span>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs" data-testid="dunning-demo-tenant">
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id} disabled={t.past_due && !t.simulated}>
                  {t.company_name}{t.is_sandbox ? " (sandbox)" : ""}{t.simulated ? " — simulated" : t.past_due ? " — really past due" : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-muted-foreground">Days already past due</span>
              <Input type="number" min={0} max={60} value={days} onChange={(e) => setDays(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)))} className="mt-1 h-8 text-xs" data-testid="dunning-demo-days" />
              <span className="text-[10px] text-muted-foreground">14+ shows the read-only lock straight away</span>
            </label>
            <label className="block">
              <span className="text-muted-foreground">Outstanding amount (£)</span>
              <Input type="number" min={1} step="0.01" placeholder="workspace MRR" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-8 text-xs" data-testid="dunning-demo-amount" />
            </label>
          </div>
          {selected?.simulated && <p className="text-violet-700" data-testid="dunning-demo-refresh-note">This workspace is already simulated — running again refreshes the dates.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="dunning-demo-cancel">Cancel</Button>
          <Button size="sm" onClick={simulate} disabled={busy || !tenantId} data-testid="dunning-demo-start">{busy ? "Simulating…" : "Simulate failed renewal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
